/**
 * 双表权威计时器（Optical 重构）。
 *
 * 数据仍来自教师端权威状态 `state.remainingMs` / `runningSide`；
 * 激活侧挂载 Matrix Orb，剩余 ≤15s 触发琥珀呼吸预警。
 */
import { SIDES, sideLabel, type Side } from '@zongheng/core-schema';
import { useArena } from '../../state/ArenaContext.tsx';
import { Badge, cx } from '../ui/primitives.tsx';
import { formatClockMs } from '../../export/report.ts';
import { MatrixOrb } from '../system/MatrixOrb.tsx';
import { CornerReticles } from '../system/Reticles.tsx';

export const CLOCK_WARNING_THRESHOLD_MS = 15_000;

function ClockCard({ side }: { side: Side }) {
  const { state } = useArena();
  const remaining = state.remainingMs[side] ?? 0;
  const budgetSec = state.stage?.budgets?.[side] ?? 0;
  const running = state.runningSide === side && state.phase === 'RUNNING';
  const warning =
    remaining > 0 && remaining <= CLOCK_WARNING_THRESHOLD_MS && state.phase === 'RUNNING';
  const exhausted = state.phase !== 'LOBBY' && budgetSec > 0 && remaining <= 0;
  const totalMs = budgetSec * 1000;
  const percent = totalMs > 0 ? Math.max(0, Math.min(100, (remaining / totalMs) * 100)) : 0;
  const accent = side === 'AFF' ? '#7DD3FC' : '#FDA4AF';

  return (
    <div
      className={cx(
        'relative min-h-[150px] overflow-hidden rounded-[14px] px-4 py-3 transition-all',
        running
          ? 'bg-white/[0.05] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.2),inset_0_0_0_1px_rgba(255,255,255,0.06),0_16px_40px_-24px_rgba(0,0,0,0.9)]'
          : 'bg-white/[0.016] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.055),inset_0_0_0_1px_rgba(255,255,255,0.02)]',
        warning && 'clock-warning',
      )}
    >
      {running && (
        <MatrixOrb
          active
          particles={560}
          accent={side}
          width={230}
          height={170}
          opacity={0.8}
          spacing={8}
          className="pointer-events-none absolute -right-9 -top-7"
        />
      )}
      <CornerReticles inset={9} color="rgba(255,255,255,0.26)" />
      <div className="relative z-[2] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{
              background: running ? accent : 'rgba(148,163,184,0.5)',
              boxShadow: running ? `0 0 10px ${accent},0 0 22px ${accent}55` : 'none',
            }}
          />
          <span className="text-[11px] font-medium tracking-[0.14em] uppercase text-slate-200">
            {sideLabel(side)}
          </span>
          <span className="micro-label">{side}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {running && <Badge tone={side === 'AFF' ? 'aff' : 'neg'}>仲裁中</Badge>}
          {exhausted && <Badge tone="violation">耗尽</Badge>}
          {warning && !exhausted && <Badge tone="warning">≤15s</Badge>}
        </div>
      </div>

      <div
        className={cx(
          'mono relative z-[2] mt-2 text-[42px] font-semibold leading-none tracking-[-0.03em]',
          warning ? 'text-clock-warning' : running ? 'text-timer' : 'text-timer-idle',
        )}
        data-testid={`coach-clock-${side.toLowerCase()}`}
        aria-label={`${sideLabel(side)}剩余时间`}
      >
        {formatClockMs(remaining)}
      </div>

      <div className="relative z-[2] mt-3 flex items-center justify-between text-[10px] text-slate-500">
        <span className="mono">BUDGET {formatClockMs(totalMs)}</span>
        <span>{running ? 'RUNNING' : 'IDLE'}</span>
      </div>
      <div className="relative z-[2] mt-1.5 h-[2px] overflow-hidden rounded-full bg-white/[0.07]">
        <div
          className="h-full rounded-full transition-[width] duration-200"
          style={{
            width: `${percent}%`,
            background: warning
              ? 'linear-gradient(90deg, rgba(252,211,77,0.5), #FCD34D)'
              : `linear-gradient(90deg, ${accent}55, ${accent})`,
            boxShadow: `0 0 8px ${warning ? 'rgba(252,211,77,0.5)' : `${accent}66`}`,
          }}
        />
      </div>
    </div>
  );
}

export function DualClockPanel() {
  const { state } = useArena();
  return (
    <section className="lens spotlight-card p-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className={cx('status-dot', state.runningSide ? 'status-dot--live' : '')} />
            <h2 className="text-[13px] font-medium tracking-wide text-slate-100">双表权威计时</h2>
            <Badge tone="blind">{state.stage?.clockMode === 'CHESS' ? 'CHESS CLOCK' : 'SINGLE'}</Badge>
          </div>
          <p className="micro-label mt-1">
            {state.stage?.clockMode === 'CHESS'
              ? '双方各自预算 · 用时耗尽自动切换发言权'
              : '单向计时 · 仅发言方预算生效'}
          </p>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-slate-500">
          <span className="micro-label">PREP</span>
          <span className="mono text-clock-warning" data-testid="coach-clock-prep">
            {state.stagePhase === 'PREP' ? formatClockMs(state.prepRemainingMs) : '—'}
          </span>
        </div>
      </header>
      <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        {SIDES.map((side) => (
          <ClockCard key={side} side={side} />
        ))}
      </div>
      {state.stage && (
        <p className="mt-3 text-[10px] text-slate-500">
          BUDGET · 正 {state.stage.budgets.AFF}s / 反 {state.stage.budgets.NEG}s · 首开{' '}
          {sideLabel(state.stage.startSide)}
          {state.stage.allowToggle ? ' · Space 可切换' : ' · 单向不可切换'}
        </p>
      )}
    </section>
  );
}
